import { useRef, useCallback } from "react";

/**
 * Hook personalizado para implementar scroll mediante arrastre del mouse
 * @param {Object} options - Opciones de configuración
 * @param {boolean} [options.horizontal=true] - Habilita el scroll horizontal
 * @param {boolean} [options.vertical=false] - Habilita el scroll vertical
 * @param {number} [options.sensitivity=1] - Sensibilidad del arrastre (1 = normal)
 * @returns {Object} Objeto con las propiedades para el elemento y la referencia
 * @returns {Object} returns.dragProps - Propiedades para aplicar al elemento que se quiere hacer arrastratable
 * @returns {React.RefObject} returns.elementRef - Referencia al elemento DOM
 */
export const useDragScroll = (options = {}) => {
  const { horizontal = true, vertical = false, sensitivity = 1 } = options;

  const elementRef = useRef(null);
  const isDragging = useRef(false);
  const startPos = useRef({ x: 0, y: 0 });
  const scrollStart = useRef({ left: 0, top: 0 });
  const hasMoved = useRef(false);
  const dragTimer = useRef(null);

  const activateDragMode = useCallback(() => {
    if (elementRef.current) {
      elementRef.current.style.cursor = "grabbing";
      elementRef.current.classList.add("is-dragging");
    }
  }, []);

  const deactivateDragMode = useCallback(() => {
    if (dragTimer.current) clearTimeout(dragTimer.current);
    isDragging.current = false;
    hasMoved.current = false;

    if (elementRef.current) {
      elementRef.current.style.cursor = "grab";
      elementRef.current.classList.remove("is-dragging");
      elementRef.current.style.userSelect = "";
    }
  }, []);

  const handleMouseDown = useCallback(
    (e) => {
      if (!elementRef.current) return;

      isDragging.current = true;
      hasMoved.current = false;
      startPos.current = {
        x: e.pageX - elementRef.current.offsetLeft,
        y: e.pageY - elementRef.current.offsetTop,
      };
      scrollStart.current = {
        left: elementRef.current.scrollLeft,
        top: elementRef.current.scrollTop,
      };

      elementRef.current.style.userSelect = "none";

      dragTimer.current = setTimeout(() => {
        if (isDragging.current && !hasMoved.current) activateDragMode();
      }, 300);
    },
    [activateDragMode]
  );

  const handleMouseMove = useCallback(
    (e) => {
      if (!isDragging.current || !elementRef.current) return;

      const currentPos = {
        x: e.pageX - elementRef.current.offsetLeft,
        y: e.pageY - elementRef.current.offsetTop,
      };
      const delta = {
        x: Math.abs(currentPos.x - startPos.current.x),
        y: Math.abs(currentPos.y - startPos.current.y),
      };

      if (delta.x > 5 || delta.y > 5) {
        hasMoved.current = true;
        e.preventDefault();

        if (dragTimer.current) clearTimeout(dragTimer.current);
        activateDragMode();

        if (horizontal)
          elementRef.current.scrollLeft =
            scrollStart.current.left -
            (currentPos.x - startPos.current.x) * sensitivity;
        if (vertical)
          elementRef.current.scrollTop =
            scrollStart.current.top -
            (currentPos.y - startPos.current.y) * sensitivity;
      }
    },
    [horizontal, vertical, sensitivity, activateDragMode]
  );

  const dragProps = {
    ref: elementRef,
    onMouseDown: handleMouseDown,
    onMouseMove: handleMouseMove,
    onMouseUp: deactivateDragMode,
    onMouseLeave: deactivateDragMode,
    style: { cursor: "grab", userSelect: "none" },
  };

  return { dragProps, elementRef };
};
